/*
  The command catalogue — every change the product can make.

  This list IS the API. It is derived from the 19 LOG_TYPES (orgchart.html:2098), every logChange()
  call site, and the mutation paths that bypassed logging entirely: saved views, filters, node
  display, pptxGeom, the column mapping, the legal-entity country field and applyDataUpdate. Those
  gaps are closed here rather than inherited — a change that is not a command cannot be attributed,
  reversed, or asked for in natural language.

  Three conventions run through the whole catalogue, and they exist for reasons worth stating:

  1. EVENTS ARE SELF-DESCRIBING WHERE RESOLUTION IS AMBIGUOUS. A bulk operation carries the explicit
     (employeeId, value) pairs it applied rather than the predicate that selected them. "Move this
     group" depends on the hierarchy under the caller's lens — filters, overrides, pivots — which the
     server does not share, so replaying a predicate later would silently hit a different set of
     people. Where resolution IS a pure function of state both sides hold (the perimeter threshold
     against dedication), the rule is stored and the resolution recomputed, because that keeps the
     rule visible as a rule.

  2. BULK COMMANDS CARRY PAIRS, SO THEY ARE THEIR OWN INVERSE. Forty people moved to Carve-out is
     forty pairs with the same value; undoing it is the same command with each person's previous
     value. No separate "restore" command exists only to be an inverse.

  3. NOTHING PER-USER APPEARS HERE. Which phase, stage, perspective or filter you are looking at is
     yours, lives in user_project_prefs, and emits no event. Broadcasting one person's lens onto
     everyone else's screen would make live editing hostile. Only the DEFINITION of a saved view is
     shared.
*/
import { z } from 'zod';
import { DEFAULT_STRANDED_MONTH, assumptionKeySchema, bucketSchema, buildUpPatchSchema, buildUpSchema, decisionSourceSchema, entityClassificationSchema, fieldKeySchema, filterConditionSchema, groupingLevelSchema, headPerspectiveSchema, idSchema, joinStageSchema, labelSchema, monthSchema, nodeDisplaySchema, perspectiveSchema, pptxGeomSchema, strandedActionSchema, targetUnitSchema, tsaPatchSchema, tsaSchema, viewGroupingSchema, viewLayoutSchema, MAX_TARGET_LEVELS, } from './domain.js';
/** Scenario-scoped commands name their scenario; the server checks it belongs to the project. */
const inScenario = { scenarioId: idSchema };
/** A value an override may hold: a level label, a job title, a salary, or a cleared field. */
const overrideValueSchema = z.union([z.string().max(500), z.number(), z.null()]);
/* ================================================================== *
 * Decisions — who goes where
 * ================================================================== */
export const setBucketCommand = z.object({
    type: z.literal('setBucket'),
    ...inScenario,
    employeeId: idSchema.describe('The person, by employee id. A planned hire (build-up) counts, but may only be Remain or Carve-' +
        'out.'),
    /** null clears the decision, returning the person to the default bucket. */
    bucket: bucketSchema.nullable().describe('Remain | Carve-out | No longer required. null clears the decision to undecided, which the ' +
        'perimeter rule may sweep.'),
    /**
     * Absent means "keep whatever is recorded, else manual".
     *
     * Not defaulted, deliberately. The original calls setBucket with no metadata from the person panel,
     * so re-asserting the bucket of someone the perimeter rule picked LEAVES them rule-owned and the
     * slider keeps governing them. Defaulting this to 'manual' would quietly promote every such click
     * into a human decision the rule may no longer touch.
     */
    src: decisionSourceSchema.optional().describe('manual or rule — who owns this decision. Omit to keep the stored owner, so a rule pick stays ' +
        'under the slider.'),
    /**
     * When a person enters the carve-out: with the dedicated perimeter, or as a Day 1 transfer.
     *
     * Explicit because the original derives it from `state.phase` (`defaultJoinStage`,
     * orgchart.html:3671) — which workflow step the user happens to be standing on. That is a per-user
     * lens the server does not have, and the fourth place this codebase reached for ambient view state
     * to decide something substantive. Absent means 'perimeter'; re-assigning someone already in the
     * carve-out keeps their existing stage unless this overrides it.
     */
    joinStage: joinStageSchema.optional().describe('Carve-out only: perimeter, or day1 for a Day 1 transfer. Omit keeps an existing member\'s stage, ' +
        'else perimeter.'),
    /** Preserved across a re-assignment unless explicitly set false. */
    day2Exit: z.boolean().optional().describe('Carve-out only: true if the position is cut again at Day 2 rightsizing. Omit keeps it, false ' +
        'clears it.'),
}).describe('Assign ONE named person to Remain, Carve-out or No longer required. Moving into Carve-out or No ' +
    'longer required also deletes any stranded mitigation plan they carry.');
export const setBucketBulkCommand = z.object({
    type: z.literal('setBucketBulk'),
    ...inScenario,
    /*
      Each assignment may carry its OWN metadata, which is what lets this command be its own inverse.
  
      It used to be `{ employeeId, bucket }` and nothing else, so the handler had to emit a per-person
      `setBucket` for every one of them — a 500-person move stored a 500-to-1000-command inverse, which
      `commandRequestSchema` (200) then refuses, so the one undo path the client could reach could not
      replay it. The reason was real and is recorded in the handler: a bulk restore carrying ONE `src` for
      everybody stamped every rule pick manual, removing them from the perimeter slider for good, and could
      not express `joinStage` or `day2Exit` at all, so every Day 1 transfer came back as a perimeter member.
  
      Per-assignment fields answer that objection instead of overriding it. The inverse is now exactly two
      commands whatever N is — one of these carrying each person's own previous metadata, plus one
      `setStrandedPlan` for the plans the forward move dropped.
  
      All three are OPTIONAL per assignment, and absent is not a default: it means "this person had none",
      which is the distinction `restoreBucket` was written to preserve. The top-level `src` below still
      applies to every assignment that names none of its own.
    */
    assignments: z
        .array(z.object({
        employeeId: idSchema,
        bucket: bucketSchema.nullable(),
        src: decisionSourceSchema.optional(),
        joinStage: joinStageSchema.optional(),
        day2Exit: z.boolean().optional(),
    }))
        .min(1)
        .max(50_000),
    /**
     * Who decided this, when the caller is asserting one.
     *
     * OPTIONAL, and deliberately not defaulted. Omitting it carries each person's stored source forward,
     * which is what the single-file app does when it bulk-assigns a group (`orgchart.html:5612` calls
     * setBucket with no meta, and :3681 falls back to the previous source). Defaulting it to 'manual'
     * silently stripped rule ownership from everyone a bulk touched — after which the perimeter slider no
     * longer governed them, with nothing on screen to say so.
     *
     * Pass 'manual' when the bulk IS the decision, as the transfer picker does.
     */
    src: decisionSourceSchema.optional().describe('Fallback owner (manual or rule) for assignments naming none. Omit to keep each person\'s stored ' +
        'owner.'),
}).describe('Assign MANY people to buckets in one command, each with their own metadata — a function, a ' +
    'filtered set, a whole selection. Same effect as setBucket per person, one history entry.');
export const setJoinStageCommand = z.object({
    type: z.literal('setJoinStage'),
    ...inScenario,
    assignments: z.array(z.object({ employeeId: idSchema, joinStage: joinStageSchema })).min(1).max(50_000).describe('One entry per person, 1..50000. Every one must already be in the Carve-out bucket, or the whole ' +
        'command fails.'),
}).describe('Re-stage people ALREADY in the carve-out: with the dedicated perimeter, or as a Day 1 transfer. ' +
    'Refuses anyone not in the carve-out, and makes their assignment manual, off the perimeter rule.');
export const setDay2ExitCommand = z.object({
    type: z.literal('setDay2Exit'),
    ...inScenario,
    assignments: z.array(z.object({ employeeId: idSchema, day2Exit: z.boolean() })).min(1).max(50_000).describe('One entry per person, 1..50000. Only read for carve-out members; a build-up in the list fails ' +
        'the command.'),
}).describe('Flag carve-out positions as cut again at Day 2 rightsizing, or clear the flag. Changes no one\'s ' +
    'bucket or join stage. Refuses planned hires — set theirs with updateBuildUp.');
export const setStrandedPlanCommand = z.object({
    type: z.literal('setStrandedPlan'),
    ...inScenario,
    assignments: z
        .array(z.object({
        employeeId: idSchema,
        /** null clears the plan, returning the person to unmitigated stranded cost. */
        action: strandedActionSchema.nullable(),
        /**
         * When the plan acts. OPTIONAL, and absent is not zero.
         *
         * It used to be `.default(0)` — "effective immediately" — while the only caller that reached a
         * user, the RemainCo panel, sent `prev ? prev.month : 6` because that is what the frozen app
         * does (`:6441`). So the platform's stated default and its actual behaviour disagreed, and a
         * caller that took the schema at its word produced a plan the product would never produce.
         *
         * Absent now means what the panel meant: keep the month this person's plan already has, and use
         * `DEFAULT_STRANDED_MONTH` if they have none. That is the one reading a re-selection of the
         * action cannot get wrong — a default of any fixed number silently MOVES an existing plan's
         * month when somebody changes only its action.
         */
        month: monthSchema.optional().describe(`Month 0-36 when the plan acts. Omit to keep an existing plan's month, else month ` +
            `${DEFAULT_STRANDED_MONTH}.`),
    }))
        .min(1)
        .max(50_000),
}).describe('Plan the RemainCo capacity a carve-out leaves stranded: rightsize or redeploy a partly dedicated ' +
    'person who is staying. Only for Remain-side people with dedication recorded.');
/**
 * The perimeter rule: everyone at or above this dedication is designated Carve-out.
 *
 * Stores the THRESHOLD, not the resulting assignments, because the rule stays visible as a rule —
 * the app shows "materialised by the ≥70% rule" against each decision, and re-running it after a
 * data update is the point. Resolution is a pure function of dedication, which both the server and
 * every client hold, so the two cannot disagree.
 */
export const applyPerimeterThresholdCommand = z.object({
    type: z.literal('applyPerimeterThreshold'),
    ...inScenario,
    threshold: z.number().min(0).max(1).describe('Dedication as a 0..1 fraction, not a percentage: 0.7 means 70% dedicated or more. At or above ' +
        'qualifies.'),
}).describe('Set the perimeter rule at a dedication threshold and RUN it now: undecided people at or above it ' +
    'become Carve-out, and its own earlier picks below it are released. Manual decisions are never ' +
    'moved.');
export const clearPerimeterThresholdCommand = z.object({
    type: z.literal('clearPerimeterThreshold'),
    ...inScenario,
}).describe('Remove the perimeter rule and release everyone it picked back to undecided. Manual buckets, Day ' +
    '1 transfers and stranded plans stay. setPerimeterRule with null drops the rule but moves nobody.');
/**
 * Record the stored rule WITHOUT re-running it.
 *
 * Every other path that writes `perimeterThreshold` also writes memberships, which is right for a
 * user setting a rule and wrong for an undo. An inverse re-executes as a command against present
 * state, so restoring the rule by re-applying it re-sweeps the roster as it stands NOW: anyone whose
 * dedication a data refresh raised, or who was moved back to Remain since the apply, is picked up by
 * the old rule and loses the stranded mitigation plan a Carve-out designation drops — with the
 * per-person restores that follow unable to cover them, because they were computed from what the
 * FORWARD command touched.
 *
 * So undo restores the rule VALUE and lets the per-person restores it already carries say who is
 * where. Deliberately reachable on its own too: recording "this is the rule we agreed" without
 * materialising it is a real thing to want, and the summary says memberships did not move.
 */
export const setPerimeterRuleCommand = z.object({
    type: z.literal('setPerimeterRule'),
    ...inScenario,
    threshold: z.number().min(0).max(1).nullable().describe('Dedication as a 0..1 fraction (0.7 = 70%); null removes the stored rule. Memberships are ' +
        'untouched either way.'),
}).describe('Record or clear the scenario\'s perimeter threshold WITHOUT re-running it — nobody changes ' +
    'bucket. For a rule agreed but not yet materialised; applyPerimeterThreshold is the one that ' +
    'moves people.');
/**
 * The governance gate: a SteerCo that has signed off a perimeter freezes it.
 *
 * While frozen, the perimeter-deciding commands (see `FREEZES_UNDER_PERIMETER`) are refused as
 * DIRECT edits for everybody, the owner included — the same posture as an archived project, and for
 * the same reason: the point of the gate is that the numbers stop moving silently. What still gets
 * through is an APPLIED PROPOSAL: somebody raises the change, somebody else reads the diff and
 * takes responsibility, and the exception is on the record with both names. Unfreezing is this
 * command again, owner-only, evented and undoable like everything else.
 */
export const setPerimeterFrozenCommand = z.object({
    type: z.literal('setPerimeterFrozen'),
    ...inScenario,
    frozen: z.boolean().describe('true freezes this scenario\'s perimeter; false lifts the freeze.'),
}).describe('Freeze or unfreeze this scenario\'s perimeter. While frozen, perimeter changes are refused as ' +
    'direct edits and land only as applied proposals — the recorded exception path.');
/* ================================================================== *
 * Structure — the shape of the organisation
 * ================================================================== */
/**
 * Designate (or explicitly un-designate) a group's head.
 *
 * `employeeId: null` means "this group has NO head" and is NOT the same as clearHead. A null says
 * somebody decided; an absent designation says nobody has, which lets a Remain view inherit the
 * as-is head. Conflating them resurrects a head the user deliberately removed.
 */
export const setHeadCommand = z.object({
    type: z.literal('setHead'),
    ...inScenario,
    perspective: headPerspectiveSchema.describe('Which org the designation belongs to: \'as-is\' (imported), \'remain\', \'carve-out\', or ' +
        '\'target\' (the blueprint).'),
    groupId: z.string().min(1).max(2000).describe('Content-addressed path of displayed level values, e.g. \'G:lvl1=Ops||lvl2=Field\'. \'G:__org__\' ' +
        'is the whole org.'),
    employeeId: idSchema.nullable().describe('The person or planned hire to head it. null records \'no head\', which also stops Remain ' +
        'inheriting the as-is head.'),
}).describe('Designate who heads a group in one perspective, or record that it has none. A planned hire may ' +
    'be named. clearHead removes the designation instead; employeeId null is a decision, not a ' +
    'removal.');
/** Remove the designation entirely, restoring inheritance. */
export const clearHeadCommand = z.object({
    type: z.literal('clearHead'),
    ...inScenario,
    perspective: headPerspectiveSchema.describe('Which org the designation belongs to: \'as-is\' (imported), \'remain\', \'carve-out\', or ' +
        '\'target\' (the blueprint).'),
    groupId: z.string().min(1).max(2000).describe('Content-addressed path of displayed level values, e.g. \'G:lvl1=Ops||lvl2=Field\'. \'G:__org__\' ' +
        'is the whole org.'),
}).describe('Delete a group\'s head designation here, so a Remain group can inherit its as-is head again. ' +
    'Distinct from setHead with employeeId null, which records that the group deliberately has no ' +
    'head.');
/**
 * Relabel a whole group within one scenario.
 *
 * `sourceValue` is the ORIGINAL (as-is) value, not the currently displayed one, so renaming twice
 * replaces the mapping instead of chaining through it — the reverse-lookup the original does before
 * writing. `newValue: null` removes the rename.
 *
 * A rename also moves the group's identity, because group ids are content-addressed on the resolved
 * level path: 'G:lvl1=Ops' becomes 'G:lvl1=Operations'. The handler therefore rewrites every head
 * designation and placement keyed to the old id in the same transaction. The single-file app does not,
 * which is why renaming a group there silently drops its head — a bug this catalogue fixes rather
 * than reproduces.
 */
export const renameGroupCommand = z.object({
    type: z.literal('renameGroup'),
    ...inScenario,
    perspective: headPerspectiveSchema.describe('\'remain\' or \'carve-out\'. \'as-is\' is refused; target units are renamed with ' +
        'renameTargetValue.'),
    levelKey: fieldKeySchema.describe('The hierarchy level the group sits at, e.g. \'lvl1\', \'lvl2\'.'),
    sourceValue: labelSchema.describe('The ORIGINAL imported value, not what is displayed now: renaming twice replaces the mapping ' +
        'rather than chaining.'),
    newValue: labelSchema.nullable().describe('The new label. null removes the rename and restores the imported name.'),
}).describe('Relabel a group within one scenario perspective; head designations follow it to the new id. ' +
    'Renaming onto a sibling\'s name merges them. Refused for \'as-is\' — correct imported data with ' +
    'setEmpField.');
/**
 * Re-parent a group: set the parent level value for everyone in it.
 *
 * Carries explicit per-person pairs rather than a group id, because which people are "in" a group
 * depends on the caller's lens. See convention 1 above.
 */
export const moveGroupCommand = z.object({
    type: z.literal('moveGroup'),
    ...inScenario,
    perspective: perspectiveSchema.describe('Use \'remain\' or \'carve-out\'. An \'as-is\' move stores overrides nothing reads; correct ' +
        'imported data with setEmpField.'),
    levelKey: fieldKeySchema.describe('The level whose value changes — the PARENT level, e.g. \'lvl1\' when moving a lvl2 team to ' +
        'another division.'),
    /** For the audit summary: which group moved, and out of where. */
    groupLabel: labelSchema.describe('Name of the group being moved. Audit summary only; it selects nobody.'),
    targetValue: labelSchema.describe('The value that level takes — the parent to move under. An existing value merges the group into ' +
        'it.'),
    employeeIds: z.array(idSchema).min(1).max(50_000).describe('Everyone in the group, listed explicitly. Avoid planned hires: the inverse (setEmpFieldBulk) ' +
        'refuses them.'),
}).describe('Re-parent a group by setting one level value for the people listed, as scenario overrides. Same ' +
    'write as setEmpFieldBulk on that level, but recorded as a group move with its old and new ' +
    'parent.');
/** Flatten a hierarchy level out of the chart within one scenario. */
export const setLevelDisabledCommand = z.object({
    type: z.literal('setLevelDisabled'),
    ...inScenario,
    perspective: headPerspectiveSchema.describe('Recorded but not applied — flattening is stored once per scenario and shows in remain and carve-' +
        'out alike.'),
    levelKey: fieldKeySchema.describe('The level to flatten, e.g. \'lvl2\'. A real hierarchy level, not a target level or a pivot ' +
        'field.'),
    disabled: z.boolean().describe('true flattens the level; false restores it.'),
}).describe('Flatten a hierarchy level out of this scenario\'s chart, or restore it: values are kept, the ' +
    'level stops forming boxes. Takes effect in remain and carve-out together; as-is is never ' +
    'flattened.');
/**
 * Edit one person's field.
 *
 * `perspective: 'as-is'` edits the base record — the imported data itself. Any other perspective
 * writes a per-scenario override and leaves the import untouched, which is what makes a scenario
 * non-destructive.
 */
export const setEmpFieldCommand = z.object({
    type: z.literal('setEmpField'),
    ...inScenario,
    perspective: headPerspectiveSchema.describe('\'remain\'/\'carve-out\' store a scenario override; \'as-is\' edits the imported record project-' +
        'wide. \'target\' is refused.'),
    employeeId: idSchema.describe('The person. A planned hire is refused — edit the position itself with updateBuildUp.'),
    fieldKey: fieldKeySchema.describe('A level key (\'lvl1\'…) or a column: name, salary, fte, dedication, jobTitle, location, country, ' +
        'seniority, legalEntity.'),
    value: overrideValueSchema.describe('Number or text. dedication is a 0..1 fraction, not a percent; openPosition/inactive take 1 or 0. ' +
        'null clears it.'),
}).describe('Edit one field for one person: a scenario override under \'remain\'/\'carve-out\', or a ' +
    'correction to the imported record under \'as-is\'. Refused for planned hires and for target ' +
    'placements.');
export const setEmpFieldBulkCommand = z.object({
    type: z.literal('setEmpFieldBulk'),
    ...inScenario,
    perspective: headPerspectiveSchema.describe('\'remain\'/\'carve-out\' store scenario overrides; \'as-is\' edits the imported records project-' +
        'wide. \'target\' is refused.'),
    fieldKey: fieldKeySchema.describe('The single field every assignment writes: a level key (\'lvl1\'…) or a column such as salary, ' +
        'jobTitle, location.'),
    assignments: z
        .array(z.object({ employeeId: idSchema, value: overrideValueSchema }))
        .min(1)
        .max(50_000),
}).describe('Set one field for many people in one change, each with their own value. Same rules as ' +
    'setEmpField. Use moveGroup when the field is a level and the point is re-parenting a whole ' +
    'group.');
/**
 * Discard a perspective's restructuring within one scenario.
 *
 * Scoped explicitly, because the original's pill says "Reset ALL restructuring" and then clears only
 * the current perspective of the active scenario (orgchart.html:4837) — leaving the sibling
 * perspective, every other scenario, and every target-org placement untouched. Naming the scope in the
 * command is the difference between a reset you can predict and one you discover.
 *
 * Clears flattened levels, per-person field overrides and group renames. Leaves buckets, stage
 * attributions, heads, build-ups, TSAs and stranded plans alone.
 */
export const resetRestructuringCommand = z.object({
    type: z.literal('resetRestructuring'),
    ...inScenario,
    /**
     * The three real perspectives, NOT `target`.
     *
     * Target placements are stored as overrides under `perspective: 'target'`, so the wider schema let
     * one call delete the entire target org for a scenario — irreversibly, and under a summary that
     * counts "moved people" and never says the word placement. Unpicking a target design is what
     * `setTargetPlacement` and `removeTargetUnit` are for.
     */
    perspective: perspectiveSchema.describe('Whose overrides and renames are discarded: \'as-is\', \'remain\' or \'carve-out\'. The target ' +
        'design is never touched.'),
}).describe('Discard a perspective\'s restructuring in this scenario — its field overrides and group renames, ' +
    'plus the scenario\'s flattened levels. Buckets, heads, TSAs, build-ups and plans stay. Cannot be ' +
    'undone.');
/* ================================================================== *
 * Build-ups — planned hires
 * ================================================================== */
export const addBuildUpCommand = z.object({
    type: z.literal('addBuildUp'),
    ...inScenario,
    buildUp: buildUpSchema,
    /**
     * The moment this row was first created, when this command is an inverse putting one back.
     *
     * Absent on every ordinary add, which is what makes it safe: a new agreement takes the clock, and only
     * an undo of a delete carries the old one. Both registers are ordered by `(created_at, id)`, so without
     * this an undone delete re-inserted with a fresh clock and the row reappeared at the BOTTOM of a list
     * the user reads down — the same defect class as a clone that drops a column, just aimed at position
     * rather than value.
     */
    restoredAt: z.string().datetime().optional().describe('Leave unset. ISO timestamp carried only by an undo, to put the row back at its old place in the ' +
        'register.'),
}).describe('Add a build-up: a planned hire not in the roster, optionally the replacement that lets a TSA ' +
    'end. It starts in Remain; setBucket is what puts it in the carve-out.');
export const updateBuildUpCommand = z.object({
    type: z.literal('updateBuildUp'),
    ...inScenario,
    buildUpId: idSchema.describe('Id of the planned hire to change; it must already exist in this scenario.'),
    patch: buildUpPatchSchema,
}).describe('Change fields on one planned hire. Only the fields named in the patch move. Setting wave can ' +
    'silently clear tsaId, neededBy or day2Exit. Remain vs carve-out is setBucket, not this.');
export const deleteBuildUpCommand = z.object({
    type: z.literal('deleteBuildUp'),
    ...inScenario,
    buildUpId: idSchema.describe('Id of the planned hire to remove.'),
}).describe('Remove a planned hire from the scenario, together with its bucket and any group it is designated ' +
    'head of. The TSA it was replacing stays, one replacement short.');
/* ================================================================== *
 * TSA register
 * ================================================================== */
export const addTsaCommand = z.object({
    type: z.literal('addTsa'),
    ...inScenario,
    tsa: tsaSchema,
    /**
     * The moment this row was first created, when this command is an inverse putting one back.
     *
     * Absent on every ordinary add, which is what makes it safe: a new agreement takes the clock, and only
     * an undo of a delete carries the old one. Both registers are ordered by `(created_at, id)`, so without
     * this an undone delete re-inserted with a fresh clock and the row reappeared at the BOTTOM of a list
     * the user reads down — the same defect class as a clone that drops a column, just aimed at position
     * rather than value.
     */
    restoredAt: z.string().datetime().optional().describe('Leave unset. ISO timestamp carried only by an undo, to put the row back at its old place in the ' +
        'register.'),
}).describe('Register a transitional service agreement in the scenario\'s TSA register: a service one side ' +
    'keeps running for the other after Day 1. The hire that replaces it is a separate build-up.');
export const updateTsaCommand = z.object({
    type: z.literal('updateTsa'),
    ...inScenario,
    tsaId: idSchema.describe('Id of the agreement to change; it must already be on the register.'),
    patch: tsaPatchSchema,
}).describe('Change fields on one agreement already on the register. Only the fields named in the patch move; ' +
    'the rest keep their values. Leaves the build-ups linked to it alone.');
export const deleteTsaCommand = z.object({
    type: z.literal('deleteTsa'),
    ...inScenario,
    tsaId: idSchema.describe('Id of the agreement to remove.'),
}).describe('Take an agreement off the register. Any build-up linked to it as the replacement survives but ' +
    'loses the link, so it stops counting as replacing anything.');
/* ================================================================== *
 * Target org design — project-scoped, except placement
 * ================================================================== */
export const setTargetEnabledCommand = z.object({
    type: z.literal('setTargetEnabled'),
    enabled: z.boolean().describe('true shows the blueprint, false hides it. Not a delete: every level, unit and placement survives ' +
        'either way.'),
}).describe('Switch the target org blueprint on or off for the project. Off hides it but keeps the ladder, ' +
    'units and placements. Enabling an empty design seeds three levels. Errors if already in that ' +
    'state.');
export const setTargetLevelsCommand = z.object({
    type: z.literal('setTargetLevels'),
    /**
     * The whole ladder, because adding, renaming and removing a level all reorder the rest.
     *
     * At least one: an empty ladder is not "no target design", it is a design whose every unit path and
     * every placement has been truncated to nothing — and the handler's shortening branch does exactly
     * that, in every scenario, with no inverse. Turning the design off is `setTargetEnabled(false)`,
     * which keeps all of it.
     */
    levels: z.array(groupingLevelSchema).min(1).max(MAX_TARGET_LEVELS).describe('The complete ladder, 1..12, outermost first — send all of them, not a delta. A rename and a ' +
        'removal cannot share a call.'),
}).describe('Replace the whole target ladder — the blueprint\'s tiers, outermost first, separate from the as-' +
    'is hierarchy. Levels may only be dropped from the end, and that deletes every placement at that ' +
    'tier.');
/**
 * Declare a unit of the target organisation, by its path.
 *
 * A declared unit exists on the chart before anybody is placed into it — that is the whole point of
 * designing top-down — so it is stored rather than derived from placements.
 */
export const addTargetUnitCommand = z.object({
    type: z.literal('addTargetUnit'),
    path: targetUnitSchema.describe('Level values top-down, e.g. [\'Commercial\',\'Sales\']. The path is the unit\'s identity; its ' +
        'depth must fit the ladder.'),
}).describe('Declare one unit (a box) of the target org by its path, before anybody is placed in it. ' +
    'Ancestors are declared too. Errors if the path already exists — declareTargetUnits declares ' +
    'many, idempotently.');
/**
 * Declare several units at once, idempotently.
 *
 * `addTargetUnit` throws when the path already exists, which is right for a person typing one name and
 * wrong for the gesture this exists to serve: "recreate this team's structure in the blueprint"
 * (orgchart.html:4638) mirrors a real group's sub-teams into the target org, and how many of those
 * already exist is not something the user chose or should have to know.
 *
 * Idempotent rather than forgiving: declaring a path that is already there is not an error and not a
 * no-op to be reported — it is the same design afterwards. The inverse removes only the paths this
 * command actually minted, so undoing it cannot take a unit somebody else declared first.
 *
 * The alternative was a client-side diff against the units it happens to be holding, plus a simulation
 * of `addTargetUnit`'s own ancestor minting. That loses the whole action to any concurrent declare, and
 * puts a rule the server owns into a React component.
 */
export const declareTargetUnitsCommand = z.object({
    type: z.literal('declareTargetUnits'),
    paths: z.array(targetUnitSchema).min(1).max(2000).describe('1..2000 unit paths, each level values top-down. Duplicates and existing paths are fine; each ' +
        'must fit the ladder depth.'),
}).describe('Declare many target units at once, idempotently — paths already declared are left alone rather ' +
    'than erring. Ancestors are declared too. Use for mirroring a real team\'s shape into the ' +
    'blueprint.');
export const removeTargetUnitCommand = z.object({
    type: z.literal('removeTargetUnit'),
    path: targetUnitSchema.describe('Full path of the unit from the top. Everything beneath it goes with it, in every scenario.'),
}).describe('Delete a declared unit and its entire sub-tree from the blueprint. Anyone placed inside moves up ' +
    'to the parent unit, or leaves the target org if the unit was top-level. Cannot be undone.');
/**
 * Rename a target level VALUE, wherever it appears in the tree.
 *
 * Not scoped by parent path, deliberately: the original renames by value, so two unrelated units both
 * called "Ops" at the same level are renamed together, and renaming into an existing sibling MERGES
 * the two. Modelling units as nodes with parent references would quietly produce different behaviour.
 */
export const renameTargetValueCommand = z.object({
    type: z.literal('renameTargetValue'),
    levelIndex: z.number().int().min(0).max(MAX_TARGET_LEVELS - 1).describe('0-based tier, 0 being the outermost level. Must exist in the current ladder.'),
    oldValue: labelSchema.describe('The existing value at that tier, matched exactly. Every unit carrying it is renamed, whatever ' +
        'its parent.'),
    newValue: labelSchema.describe('The replacement value. If a sibling at that tier already has it, the two units merge into one.'),
}).describe('Rename a level value wherever it appears at that tier, so two unrelated units both called ' +
    '\'Ops\' rename together. Renaming onto an existing sibling MERGES the two units, which cannot be ' +
    'undone.');
/**
 * Re-parent a declared unit and its whole subtree. An empty `toParent` promotes it to the top.
 *
 * The handler's order of operations is load-bearing: reject a move into the unit's own branch, then
 * compute the moved subtree's maximum depth and grow the level ladder BEFORE writing anything (or the
 * deep tail of the subtree is silently truncated), then repath units and placements, then materialise
 * the destination's implicit ancestors so the boxes exist, then dedupe — which is also how a move onto
 * an existing sibling merges.
 */
export const reparentTargetUnitCommand = z.object({
    type: z.literal('reparentTargetUnit'),
    path: targetUnitSchema.describe('Full path of the unit to move, from the top. Its sub-units and placements follow, in every ' +
        'scenario.'),
    toParent: z.array(labelSchema).max(MAX_TARGET_LEVELS).describe('The new parent\'s full path; an empty array promotes to the top. May not sit inside the moved ' +
        'unit.'),
}).describe('Move one declared unit, with its sub-tree and everyone placed in it, under a different parent. ' +
    'Moving onto a slot that is already occupied merges the two units and cannot be undone.');
/** Place a person into a target unit. Scenario-scoped: placement is a design decision. */
export const setTargetPlacementCommand = z.object({
    type: z.literal('setTargetPlacement'),
    ...inScenario,
    assignments: z
        .array(z.object({ employeeId: idSchema, path: targetUnitSchema.nullable() }))
        .min(1)
        .max(50_000),
}).describe('Put people into declared target-org units, or take them out. Scenario-scoped, unlike the units ' +
    'themselves. Not a carve-out/RemainCo decision — that is setBucket — and the target design must ' +
    'be on.');
/* ================================================================== *
 * Scenarios
 * ================================================================== */
export const createScenarioCommand = z.object({
    type: z.literal('createScenario'),
    scenarioId: idSchema,
    name: labelSchema,
});
export const duplicateScenarioCommand = z.object({
    type: z.literal('duplicateScenario'),
    /** The scenario being copied. */
    sourceScenarioId: idSchema,
    /** The new scenario's id, chosen by the caller so the response is not needed to continue. */
    scenarioId: idSchema,
    name: labelSchema,
});
export const renameScenarioCommand = z.object({
    type: z.literal('renameScenario'),
    scenarioId: idSchema,
    name: labelSchema,
});
export const deleteScenarioCommand = z.object({
    type: z.literal('deleteScenario'),
    scenarioId: idSchema,
});
export const setDefaultScenarioCommand = z.object({
    type: z.literal('setDefaultScenario'),
    scenarioId: idSchema,
});
/* ================================================================== *
 * Legal entities
 * ================================================================== */
export const addEntityCommand = z.object({
    type: z.literal('addEntity'),
    entityId: idSchema.describe('A new registry id you choose; it must not already exist in this project. An entity, not a person ' +
        'or a scenario.'),
    name: labelSchema.describe('The entity\'s legal name. Refused if another entity already has it, ignoring case and ' +
        'surrounding spaces.'),
    country: labelSchema.nullable().default(null).describe('Country of incorporation. null means unrecorded, not a country.'),
    classification: entityClassificationSchema.default('retain').describe('How the deal treats it: retain, transfer, new or dissolve. Defaults to retain, as any ' +
        'unregistered entity reads.'),
}).describe('Add a legal entity to the project\'s registry, the list that classifies each entity in the deal. ' +
    'Project-wide, and it moves nobody: transferring people between entities is remapEntity.');
export const updateEntityCommand = z.object({
    type: z.literal('updateEntity'),
    entityId: idSchema.describe('The registry row to edit; it must already exist. An entity id, not a person\'s.'),
    patch: z
        .object({
        name: labelSchema,
        /** Editing this bypassed the change log entirely in the original. */
        country: labelSchema.nullable(),
        classification: entityClassificationSchema,
    })
        .partial(),
}).describe('Edit a registry entity\'s name, country or classification. A rename follows the name into every ' +
    'scenario\'s entity mappings and per-person overrides; nobody moves between entities.');
export const deleteEntityCommand = z.object({
    type: z.literal('deleteEntity'),
    entityId: idSchema.describe('The registry row to remove; it must exist. Employees keep this entity as their stored legal-' +
        'entity value.'),
}).describe('Remove an entity from the project registry. Nobody moves and no legal-entity value changes: ' +
    'people still carrying its name fall back to the default retain classification.');
/**
 * Transfer people from one legal entity to another.
 *
 * Scenario-scoped and override-based: an entity transfer is a design decision, not a correction to
 * the imported data.
 */
export const remapEntityCommand = z.object({
    type: z.literal('remapEntity'),
    ...inScenario,
    perspective: perspectiveSchema.describe('remain or carve-out. as-is is accepted but nothing reads it: the as-is view is always the ' +
        'imported data.'),
    fromEntity: labelSchema.describe('The imported (as-is) entity name people are leaving, not a name an earlier remap produced.'),
    toEntity: labelSchema.describe('The entity name they read as instead. Equal to fromEntity, or blank, removes the mapping rather ' +
        'than storing it.'),
    employeeIds: z.array(idSchema).min(1).max(50_000).describe('Who the transfer is about, for the audit summary and its count. Each must exist; the mapping is ' +
        'not limited to them.'),
}).describe('Transfer people between legal entities in one scenario and perspective. Stored as a mapping on ' +
    'the entity NAME, so it moves everyone whose imported entity is fromEntity, not only the ids ' +
    'listed.');
/* ================================================================== *
 * Assumptions
 * ================================================================== */
/**
 * Set one cost assumption.
 *
 * The two severance grids are keyed by the client's own country and grade values, so a write to one
 * names a `subKey`; a null value removes that tier, which is not the same as setting it to zero — an
 * absent tier inherits the next rule down (seniority, then country, then default).
 *
 * `value` stays a union rather than a number because an editor posts what its input box holds, and a
 * blank box is `''`. The handler treats blank as removal on a GRID and refuses it on a scalar, which is
 * the one asymmetry worth stating out loud: `Number('')` is 0, so coercing a blank grid cell would
 * price a whole country at no severance while the engine reads the same value as *inherit*. A stored
 * zero has to be typed.
 *
 * `subKey` is not `labelSchema` alone: a blank or prototype key writes a tier `lookup` can never read
 * back (`cost.ts:47-51`), so the handler rejects those rather than reporting success for a write that
 * moves no number.
 */
export const setAssumptionCommand = z.object({
    type: z.literal('setAssumption'),
    key: assumptionKeySchema.describe('loadedCostMultiplier (× salary), severanceMonthsDefault and both severance grids (months), ' +
        'hiringCostPct (% of salary), fxRates (per currency code, the rate into the reporting currency; ' +
        'absent = × 1), reportingCurrency (a label like EUR — never arithmetic), day1Date (ISO date the ' +
        'month clock counts from; a label, arithmetic stays on indices), consultationMonthsByCountry ' +
        '(months before a departure can lawfully take effect — a warning input for diagnostics), ' +
        'retentionPct (% of salary, one-off retention for Day 2 exits and stranded rightsizings; 0 = none), ' +
        'loadedCostMultiplierByCountry (per-country multiplier overriding the global), ' +
        'closeMonthByCountry (whole months after Day 1 a country\'s transfer closes; absent = Day 1 — ' +
        'shifts when stranded cost starts and feeds timing warnings).'),
    subKey: labelSchema.nullable().default(null).describe('Required for the grids — the country, grade or currency code the tier prices, matched ' +
        'exactly. Must be null otherwise.'),
    value: z.union([z.number(), z.string(), z.null()]).describe('A number — 0 is a real override, not a removal (fx rates refuse 0: a zero rate zeroes salaries). ' +
        'null removes a grid tier so the fallback applies; numeric scalars refuse blank; ' +
        'reportingCurrency takes a short string, blank to unname.'),
}).describe('Set one cost assumption for the project — global, so every scenario reprices. Severance resolves ' +
    'seniority tier, then country, then the default. Economics surfaces only; org views keep raw ' +
    'salary.');
/* ================================================================== *
 * Intake and data maintenance
 * ================================================================== */
/**
 * Re-map which source column feeds which logical field.
 *
 * Edited live inside the mapping modal in the original, with no log entry and outside undo — so a
 * mapping mistake was silently unrecoverable. The whole map is sent, because changing one mapping
 * can require changing another (two fields cannot share a column).
 */
export const setColumnMappingCommand = z.object({
    type: z.literal('setColumnMapping'),
    columnMap: z.record(fieldKeySchema, z.string().max(500)),
});
/** Declare the hierarchy levels and their labels. */
export const setGroupingLevelsCommand = z.object({
    type: z.literal('setGroupingLevels'),
    levels: z.array(groupingLevelSchema).max(12),
});
/**
 * The joiners / leavers / movers refresh: apply a staged extract.
 *
 * A fresh extract arrives mid-engagement and the roster has to move with it without losing the design
 * built on top. The file is uploaded and staged first, and this command names the run — it does NOT
 * carry the diff. That is a correction to an earlier draft of this catalogue, and the reasons are worth
 * keeping because each was a real defect:
 *
 *   · A diff computed at preview time and applied later classifies people against a roster that two
 *     other users may have moved on. The original gets away with it because its Apply button lives
 *     inside a modal that blocks undo; a server has no such window. Computing the diff at apply time,
 *     from the same pure function the preview used, means the two cannot disagree.
 *   · A payload carrying raw source rows would persist them verbatim in change_events, which is
 *     immutable and has no redaction path — and an unmapped column in an HR extract can carry a home
 *     address or a bank account. Staged rows are deletable and expire.
 *   · A payload cannot state a joiner's identity reliably: ids are synthesised positionally when the
 *     source has none, so identity would depend on the array index.
 *   · An approver can check provenance — which file, uploaded by whom, when — rather than trusting an
 *     id nothing validates.
 *
 * Leavers are marked, never deleted: their severance still prices, they still anchor the audit trail,
 * and keeping the row is what stops a head designation pointing at somebody who no longer exists.
 *
 * In the original this path rewrote state.employees wholesale, emitted no reversible entry, and then
 * discarded the entire undo history — making every earlier design step unrevertable too. Here it is one
 * atomic all-or-nothing change with a complete audit entry. It is deliberately NOT undoable: a refresh
 * changes the population, and recovering from one is what PROJECT point-in-time restore is for. The
 * project, not the scenario: the roster this replaces is project-scoped, and so is this command — it
 * records `scenario_id = NULL`, so there is no scenario a restore could be aimed at.
 */
export const applyDataUpdateCommand = z.object({
    type: z.literal('applyDataUpdate'),
    /** A staged run, in this project, not yet applied. */
    runId: idSchema,
    /**
     * The row count the preview was shown, as a concurrency check.
     *
     * Cheap, and it catches the case the original cannot: if the roster changed between the preview and
     * the apply, the numbers the approver agreed to are not the numbers about to be written.
     */
    expectRosterCount: z.number().int().nonnegative().optional(),
});
/* ================================================================== *
 * Saved views and display configuration
 * ================================================================== */
export const saveViewCommand = z.object({
    type: z.literal('saveView'),
    viewId: idSchema.describe('A fresh id for the new view; fails if that id already exists. Editing an existing one is ' +
        'updateView.'),
    name: labelSchema.describe('Label shown in the view picker, up to 500 characters.'),
    viewGrouping: viewGroupingSchema.nullable().default(null).describe('Pivot by these fields, outermost first, max 6. null or [] keeps the project\'s own grouping ' +
        'levels.'),
    filters: z.array(filterConditionSchema).max(20).default([]).describe('Who the view shows: AND across fields, OR within one field\'s values, max 20. Display only, no ' +
        'bucket change.'),
    viewLayout: viewLayoutSchema.default('grouping').describe('\'grouping\' uses the pivot or grouping levels; \'reporting\' uses the supervisor line and needs ' +
        'a mapped supervisor column.'),
}).describe('Save a new named view: the lens of pivot, filters and layout the chart is read through. Viewing ' +
    'only, it moves nobody between buckets. A project\'s first saved view also becomes its default.');
export const updateViewCommand = z.object({
    type: z.literal('updateView'),
    viewId: idSchema.describe('The existing view to change; fails if this project has no such view.'),
    patch: z
        .object({
        name: labelSchema,
        viewGrouping: viewGroupingSchema.nullable(),
        filters: z.array(filterConditionSchema).max(20),
        viewLayout: viewLayoutSchema,
    })
        .partial(),
}).describe('Edit a saved view in place, keeping its id and whether it is the default. Only the keys present ' +
    'in the patch change. saveView creates a new view; setDefaultView chooses which one opens.');
export const deleteViewCommand = z
    .object({ type: z.literal('deleteView'), viewId: idSchema })
    .describe('Remove a saved view. Only the lens goes: no employee, bucket, TSA or scenario data changes with ' +
    'it. If it was the default, the project falls back to opening on the canonical hierarchy.');
export const setDefaultViewCommand = z.object({
    type: z.literal('setDefaultView'),
    /** null clears the default, so the project opens on the canonical hierarchy. */
    viewId: idSchema.nullable().describe('An existing saved view; null clears the default, so the project opens on the canonical ' +
        'hierarchy.'),
}).describe('Choose which saved view the whole project opens on. It does not touch that view\'s pivot or ' +
    'filters, which is updateView\'s job.');
/** Shared, not personal: the deck exporter reads the same config, so there is one per project. */
export const setNodeDisplayCommand = z.object({
    type: z.literal('setNodeDisplay'),
    nodeDisplay: nodeDisplaySchema.describe('Complete replacement, not a patch: send every key. Colours are six hex digits without \'#\'; box ' +
        'sizes in inches.'),
}).describe('Set what the person, group and root cards show, plus bucket colours and node box sizes. One ' +
    'answer per project, shared with the deck exporter, not a per-user preference.');
export const setPptxGeomCommand = z.object({
    type: z.literal('setPptxGeom'),
    pptxGeom: pptxGeomSchema.describe('Both boxes in full, each {x, y, w, h} in centimetres from the slide\'s top-left corner; not a ' +
        'patch.'),
}).describe('Set where the title and content boxes sit on an exported slide, in centimetres. The slide frame ' +
    'only: what the cards show and how big they are is setNodeDisplay.');
/* ================================================================== *
 * Project and notes
 * ================================================================== */
export const renameProjectCommand = z.object({
    type: z.literal('renameProject'),
    name: labelSchema.min(1),
});
/**
 * Archive or restore the project. Reversible, and an archived project is read-only.
 *
 * Distinct from deletion, which is not a command: destroying a body of work with named employees and
 * salaries in it is an owner-only endpoint that requires the project name typed back, not something
 * reachable from the same surface as a bucket change.
 */
export const archiveProjectCommand = z.object({
    type: z.literal('archiveProject'),
    archived: z.boolean(),
});
/**
 * Rebuild the roster from the retained source rows under the current column mapping.
 *
 * Named for what it destroys. In the single-file app this is what re-confirming the column-mapping
 * modal does (orgchart.html:3584) — it deletes every scenario, the entire change log, all heads,
 * buckets, build-ups, overrides and saved views, with no confirmation and no undo, and the modal is
 * reachable long after import. Here that behaviour is a separate, explicit, owner-only command, and
 * `setColumnMapping` no longer carries it.
 */
export const rebuildRosterFromSourceCommand = z.object({
    type: z.literal('rebuildRosterFromSource'),
    /** Must equal the project's name. A confirmation, not a rename. */
    confirmProjectName: labelSchema.min(1),
});
/**
 * Put the whole project back to a recorded restore point.
 *
 * A POINT, not an arbitrary sequence number, and that is the honest shape rather than a compromise. The
 * points that exist are written immediately before every change that cannot be undone, plus a cadence —
 * so they are exactly the moments the five "restore from a point in time" messages are about. "Restore
 * the project to the point before this change" is a promise the storage can keep; "to any point in time"
 * is not.
 *
 * PROJECT-scoped, settled in P5a on evidence: `seq` is allocated by bumping `projects.seq` under the
 * project row lock and `change_events` is unique on `(project_id, seq)`, so "this scenario at seq N" is
 * not a coordinate the log defines.
 *
 * Owner-only and confirmed by typing the project name, for the same reason `rebuildRosterFromSource` is:
 * it replaces everybody's work at once, including work done in scenarios the restorer is not looking at.
 */
export const restoreProjectCommand = z.object({
    type: z.literal('restoreProject'),
    /** The recorded point to become. The state AS OF this seq, not the state this seq produced. */
    seq: z.number().int().nonnegative(),
    /** Must equal the project's name. A confirmation, not a rename. */
    confirmProjectName: labelSchema.min(1),
});
/**
 * Issue a SteerCo pack: the deck and the workbook rendered as one act, from one moment.
 *
 * The event is the RECORD of that act — it changes no design state, the way `addNote` changes none —
 * and it is what makes the pack provable: the restore point stamped immediately before it (the
 * `NEVER_AN_INVERSE` machinery does that) is the exact state the files were rendered from, and the
 * change log between two of these events IS "what moved since the last SteerCo".
 *
 * Only the pack route may run it. A bare `issueSteercoPack` through `/commands` would put a row in
 * the log claiming files exist that were never rendered — the server refuses it there, so the event
 * and the artefacts cannot come apart.
 */
export const issueSteercoPackCommand = z.object({
    type: z.literal('issueSteercoPack'),
    /** What the committee calls this pack — "SteerCo 4", "Signing board". Names the restore point too. */
    name: labelSchema.min(1),
});
/** A standalone entry in the history: the workshop decision behind a set of changes. */
export const addNoteCommand = z.object({
    type: z.literal('addNote'),
    scenarioId: idSchema.nullable().default(null),
    note: z.string().min(1).max(4000).describe('The remark itself, 1 to 4000 characters, and the whole content of the entry.'),
}).describe('Put a standalone entry in the history: the workshop decision or reasoning behind a run of ' +
    'changes. Writes no design state. To hang a remark on one change already made, use annotateEvent.');
/** Annotate an existing change. The event stays immutable; the note is a separate projection. */
export const annotateEventCommand = z.object({
    type: z.literal('annotateEvent'),
    eventId: idSchema.describe('The change to annotate, by its history event id, in this project. Not an employee, view or ' +
        'scenario id.'),
    note: z.string().max(4000).describe('Up to 4000 characters. An empty string removes the note currently on that change.'),
}).describe('Record the why against one change already in the history log. Changes neither that change nor ' +
    'the design, and replaces any note already on it. For a free-standing entry use addNote.');
/* ================================================================== *
 * The union
 * ================================================================== */
export const commandSchema = z.discriminatedUnion('type', [
    // decisions
    setBucketCommand,
    setBucketBulkCommand,
    setJoinStageCommand,
    setDay2ExitCommand,
    setStrandedPlanCommand,
    applyPerimeterThresholdCommand,
    clearPerimeterThresholdCommand,
    setPerimeterRuleCommand,
    setPerimeterFrozenCommand,
    // structure
    setHeadCommand,
    clearHeadCommand,
    renameGroupCommand,
    moveGroupCommand,
    setLevelDisabledCommand,
    setEmpFieldCommand,
    setEmpFieldBulkCommand,
    resetRestructuringCommand,
    // build-ups
    addBuildUpCommand,
    updateBuildUpCommand,
    deleteBuildUpCommand,
    // TSAs
    addTsaCommand,
    updateTsaCommand,
    deleteTsaCommand,
    // target org
    setTargetEnabledCommand,
    setTargetLevelsCommand,
    addTargetUnitCommand,
    declareTargetUnitsCommand,
    removeTargetUnitCommand,
    renameTargetValueCommand,
    reparentTargetUnitCommand,
    setTargetPlacementCommand,
    // scenarios
    createScenarioCommand,
    duplicateScenarioCommand,
    renameScenarioCommand,
    deleteScenarioCommand,
    setDefaultScenarioCommand,
    // entities
    addEntityCommand,
    updateEntityCommand,
    deleteEntityCommand,
    remapEntityCommand,
    // assumptions
    setAssumptionCommand,
    // intake
    setColumnMappingCommand,
    setGroupingLevelsCommand,
    applyDataUpdateCommand,
    // views and display
    saveViewCommand,
    updateViewCommand,
    deleteViewCommand,
    setDefaultViewCommand,
    setNodeDisplayCommand,
    setPptxGeomCommand,
    // project and notes
    renameProjectCommand,
    archiveProjectCommand,
    rebuildRosterFromSourceCommand,
    restoreProjectCommand,
    issueSteercoPackCommand,
    addNoteCommand,
    annotateEventCommand,
]);
/** Every command the catalogue declares, derived from the schema rather than restated. */
export const COMMAND_TYPES = commandSchema.options.map((option) => option.shape.type.value);
/**
 * Commands that address a scenario. The rest are project-scoped.
 *
 * Kept as data because the server needs it before it can validate that a scenario belongs to the
 * project being addressed — the check that stops a member of project A writing into project B's
 * scenario by naming its id.
 */
export function scenarioIdOf(command) {
    return 'scenarioId' in command && typeof command.scenarioId === 'string'
        ? command.scenarioId
        : null;
}
/**
 * Commands only an owner may issue.
 *
 * Everything that changes who can see the project, destroys a body of work, or rewrites the imported
 * roster wholesale. An editor designs; an owner decides what the project IS.
 */
/**
 * Commands that CREATE the scenario they name, so it must NOT already exist.
 *
 * The server checks a named scenario belongs to the project before running a handler — the check that
 * stops one project writing into another's scenario. These two are the exception: their scenarioId is
 * the thing being made. duplicateScenario's SOURCE is validated instead.
 */
export const SCENARIO_CREATING_COMMANDS = new Set([
    'createScenario',
    'duplicateScenario',
]);
/**
 * The commands whose `scenarioId` NAMES a scenario rather than saying which one is being designed in.
 *
 * The distinction is invisible in the schemas — every one of them is `scenarioId: idSchema` — and it
 * decides whether the field can be supplied by context instead of by the caller. For `setBucket` the
 * scenario is the workspace: "put this person in the carve-out" means in the design under discussion.
 * For these five it is the OBJECT of the sentence: create THIS one, rename THAT one, make THAT one the
 * default. Two of them (`SCENARIO_CREATING_COMMANDS`) name a scenario that must not exist yet.
 *
 * Written out rather than derived because no property of the schemas separates the two readings — the
 * criterion is what the command MEANS. The agent surface asserts against this list, so a sixth member
 * is a deliberate act rather than a silent inclusion.
 */
export const SCENARIO_LIFECYCLE_COMMANDS = new Set([
    'createScenario',
    'duplicateScenario',
    'renameScenario',
    'deleteScenario',
    'setDefaultScenario',
]);
/**
 * The commands a reader may issue: commentary, which changes nothing.
 *
 * Both write no design state and return an empty inverse, so nothing here can alter a decision or be
 * reverted into one. The set is the gate, so adding a third member is a deliberate act and the
 * authorisation matrix asserts against this list rather than a hand-written copy of it.
 */
export const COMMENTARY_COMMANDS = new Set([
    'addNote',
    'annotateEvent',
]);
export const OWNER_ONLY_COMMANDS = new Set([
    'renameProject',
    'deleteScenario',
    'setColumnMapping',
    'setGroupingLevels',
    'applyDataUpdate',
    'rebuildRosterFromSource',
    'archiveProject',
    // Replaces everybody's design at once, including scenarios the restorer is not looking at.
    'restoreProject',
    // Declaring or lifting a governance gate is the SteerCo's act, spoken through the owner.
    'setPerimeterFrozen',
]);
/**
 * Which commands a frozen perimeter refuses as direct edits.
 *
 * `satisfies Record<CommandType, boolean>` IS THE GUARD, same as `COMMAND_REACH`: a new command is
 * a build error here until somebody decides whether a frozen perimeter blocks it — and the
 * dangerous default is "false", which reads as allowed.
 *
 * TRUE = the command decides who is in the carve-out, on which side of Day 1 they join, or what the
 * stored rule says — the exact facts a SteerCo signs off. FALSE for four populations worth
 * arguing out loud:
 *  · `applyDataUpdate` / `rebuildRosterFromSource` — owner-only and about DATA REALITY: a roster
 *    refresh moves a frozen perimeter because people genuinely left, and refusing it would block
 *    hygiene to protect a fiction. The change log records what moved.
 *  · `restoreProject` — a different argument from data reality: a restore is design rollback,
 *    owner-only and snapshot-stamped, and it replaces every scenario's design wholesale, frozen or
 *    not. What it must NOT move is the gate itself — the restore machinery strips
 *    `perimeter_frozen` from the payload (migration 0021, the `archived_at` treatment), so the
 *    design rolls back UNDER a gate that stays where the owner last put it.
 *  · `setPerimeterFrozen` itself — the gate must not lock its own key in the box (the
 *    `archiveProject` self-exemption, restated).
 *  · Everything Day-2 and RemainCo (`setDay2Exit`, `setStrandedPlan`, TSAs, build-ups, target),
 *    and the scenario lifecycle (create/duplicate/rename/setDefault) — the freeze governs the
 *    PERIMETER of one scenario, not the whole design and not the project's scenario politics. A
 *    duplicate is deliberately born unfrozen: it is a draft, and frozen marks sign-off, which a
 *    draft does not have.
 */
export const FREEZES_UNDER_PERIMETER = {
    // ---- decisions
    setBucket: true,
    setBucketBulk: true,
    setJoinStage: true,
    setDay2Exit: false,
    setStrandedPlan: false,
    applyPerimeterThreshold: true,
    clearPerimeterThreshold: true,
    setPerimeterRule: true,
    setPerimeterFrozen: false,
    // ---- structure
    setHead: false,
    clearHead: false,
    renameGroup: false,
    moveGroup: false,
    setLevelDisabled: false,
    setEmpField: false,
    setEmpFieldBulk: false,
    resetRestructuring: false,
    // ---- build-ups
    addBuildUp: false,
    updateBuildUp: false,
    deleteBuildUp: false,
    // ---- TSAs
    addTsa: false,
    updateTsa: false,
    deleteTsa: false,
    // ---- target org
    setTargetEnabled: false,
    setTargetLevels: false,
    addTargetUnit: false,
    declareTargetUnits: false,
    removeTargetUnit: false,
    renameTargetValue: false,
    reparentTargetUnit: false,
    setTargetPlacement: false,
    // ---- scenarios
    createScenario: false,
    duplicateScenario: false,
    renameScenario: false,
    deleteScenario: false,
    setDefaultScenario: false,
    // ---- entities
    addEntity: false,
    updateEntity: false,
    deleteEntity: false,
    remapEntity: false,
    // ---- assumptions
    setAssumption: false,
    // ---- intake
    setColumnMapping: false,
    setGroupingLevels: false,
    applyDataUpdate: false,
    // ---- views and display
    saveView: false,
    updateView: false,
    deleteView: false,
    setDefaultView: false,
    setNodeDisplay: false,
    setPptxGeom: false,
    // ---- project and notes
    renameProject: false,
    archiveProject: false,
    rebuildRosterFromSource: false,
    restoreProject: false,
    /*
      A pack RECORDS the perimeter, it does not move it — and recording is precisely what a frozen
      perimeter is for: the SteerCo that froze it is the audience the pack is rendered at.
    */
    issueSteercoPack: false,
    addNote: false,
    annotateEvent: false,
};
export const COMMAND_REACH = {
    // ---- decisions, all held against people
    setBucket: 'named',
    setBucketBulk: 'named',
    setJoinStage: 'named',
    setDay2Exit: 'named',
    setStrandedPlan: 'named',
    setTargetPlacement: 'named',
    /*
      The rule is evaluated over everyone (`previewPerimeterThreshold`), so a narrowed version of it
      would silently mean something else. `setPerimeterRule` merely RECORDS the number without moving
      anybody, so it names nobody and is project-wide — the pair is deliberately split that way in the
      catalogue and the split is exactly what makes this distinction available.
    */
    applyPerimeterThreshold: 'roster-wide',
    clearPerimeterThreshold: 'roster-wide',
    setPerimeterRule: 'project-wide',
    // The freeze flag, like the rule value: names nobody, so a scoped owner could set it — moot
    // today, since owners are unrestricted by constraint, but the classification is about the payload.
    setPerimeterFrozen: 'project-wide',
    // ---- structure
    setHead: 'named',
    clearHead: 'project-wide',
    renameGroup: 'project-wide',
    moveGroup: 'named',
    setLevelDisabled: 'project-wide',
    setEmpField: 'named',
    setEmpFieldBulk: 'named',
    /*
      Discards every override and rename for a perspective across the scenario. Like the threshold, its
      effect is a statement about the whole population rather than about the people it lists — because
      it lists none.
    */
    resetRestructuring: 'roster-wide',
    /*
      A planned hire names the person they REPORT TO, which is the id the handler validates against the
      roster. That makes it a named command for scoping too: placing a hire under a manager you cannot
      see is a change to a part of the organisation you were not given.
    */
    addBuildUp: 'named',
    updateBuildUp: 'named',
    deleteBuildUp: 'named',
    // ---- registers and settings, which name no employee
    addTsa: 'project-wide',
    updateTsa: 'project-wide',
    deleteTsa: 'project-wide',
    setTargetEnabled: 'project-wide',
    setTargetLevels: 'project-wide',
    addTargetUnit: 'project-wide',
    declareTargetUnits: 'project-wide',
    removeTargetUnit: 'project-wide',
    renameTargetValue: 'project-wide',
    reparentTargetUnit: 'project-wide',
    createScenario: 'project-wide',
    duplicateScenario: 'project-wide',
    renameScenario: 'project-wide',
    deleteScenario: 'project-wide',
    setDefaultScenario: 'project-wide',
    addEntity: 'project-wide',
    updateEntity: 'project-wide',
    deleteEntity: 'project-wide',
    remapEntity: 'named',
    setAssumption: 'project-wide',
    setColumnMapping: 'project-wide',
    setGroupingLevels: 'project-wide',
    saveView: 'project-wide',
    updateView: 'project-wide',
    deleteView: 'project-wide',
    setDefaultView: 'project-wide',
    setNodeDisplay: 'project-wide',
    setPptxGeom: 'project-wide',
    renameProject: 'project-wide',
    archiveProject: 'project-wide',
    addNote: 'project-wide',
    annotateEvent: 'project-wide',
    /*
      THE THREE THAT REPLACE THE POPULATION. A refresh is diffed against the whole roster; a rebuild
      re-derives every person from the source rows; a restore replaces the entire design including
      scenarios the caller is not looking at. All three are already owner-only, and owners are never
      scoped — so classifying them is belt-and-braces rather than the load-bearing part, and it is
      written down because "it cannot happen" is how the next exception gets missed.
    */
    applyDataUpdate: 'roster-wide',
    rebuildRosterFromSource: 'roster-wide',
    restoreProject: 'roster-wide',
    /*
      Names nobody: the pack photographs the project without touching a person. The route that issues
      one refuses restricted grants for a different reason — the ARTEFACT is whole-perimeter, like the
      buyer pack — but that is the route's disclosure rule, not a fact about this command's payload.
    */
    issueSteercoPack: 'project-wide',
};
//# sourceMappingURL=commands.js.map